import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
} from "reactstrap";

import { AppVideoPlayer } from "../../components";
import imagePlaceholder from "../../assets/images/placeholder/image-placeholder.png";
import { LiveLectureDto } from "../../interfaces";
import { MediaDto, MediaStatusEnum } from "../../interfaces/media";
import { useUpdateMediaStatusMutation } from "../../store/api/mediaApi";
import {
  lectureRecordings,
  recordingPlayableUrl,
  recordingSortTime,
} from "./recordingMedia";

type LiveLectureRecordingsProps = {
  recordings: MediaDto[];
};

const statusColor = (status?: MediaStatusEnum) => {
  switch (status) {
    case MediaStatusEnum.PROCESSING:
      return "warning";
    case MediaStatusEnum.COMPLETED:
      return "success";
    case MediaStatusEnum.FAILED:
      return "danger";
    default:
      return "secondary";
  }
};

export const LiveLectureRecordings: React.FC<LiveLectureRecordingsProps> = ({
  recordings,
}) => {
  const { t, i18n } = useTranslation();
  const [playing, setPlaying] = useState<MediaDto | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [updateMediaStatus] = useUpdateMediaStatusMutation();
  const playingUrl = playing ? recordingPlayableUrl(playing) : null;

  const retryConversion = async (mediaId: string) => {
    setRetryingId(mediaId);
    try {
      await updateMediaStatus({
        id: mediaId,
        status: MediaStatusEnum.NEW,
      }).unwrap();
    } finally {
      setRetryingId(null);
    }
  };

  const formatWhen = (item: MediaDto) => {
    const time = recordingSortTime(item);
    if (!time) {
      return t("liveLectureRecording", { defaultValue: "Recording" });
    }
    return new Date(time).toLocaleString(i18n.language, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const statusLabel = (status?: MediaStatusEnum) => {
    switch (status) {
      case MediaStatusEnum.PROCESSING:
        return t("liveLectureRecordingInProgress", {
          defaultValue: "In progress",
        });
      case MediaStatusEnum.COMPLETED:
        return t("completed");
      case MediaStatusEnum.FAILED:
        return t("failed");
      default:
        return t("liveLectureRecordingWaiting", { defaultValue: "Waiting" });
    }
  };

  if (!recordings.length) {
    return null;
  }

  return (
    <>
      <div className="d-flex flex-column gap-2">
        {recordings.map((item) => {
          const playableUrl = recordingPlayableUrl(item);
          const pending =
            item.status === MediaStatusEnum.NEW ||
            item.status === MediaStatusEnum.PROCESSING ||
            !item.status;

          return (
            <div
              key={item.id}
              className="d-flex flex-wrap align-items-center justify-content-between gap-2 border rounded p-2"
            >
              <div className="d-flex align-items-center gap-2 min-w-0">
                <button
                  type="button"
                  className="position-relative border-0 p-0 bg-transparent flex-shrink-0 rounded overflow-hidden"
                  style={{ width: 96, height: 54 }}
                  disabled={!playableUrl}
                  aria-label={formatWhen(item)}
                  onClick={() => playableUrl && setPlaying(item)}
                >
                  <img
                    src={item.thumbnail || imagePlaceholder}
                    alt=""
                    className="w-100 h-100"
                    style={{ objectFit: "cover" }}
                  />
                  {playableUrl ? (
                    <span
                      className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center rounded-circle text-white"
                      style={{
                        width: 28,
                        height: 28,
                        background: "rgba(0,0,0,0.55)",
                      }}
                    >
                      <i className="ri-play-fill"></i>
                    </span>
                  ) : null}
                </button>
                <div className="min-w-0">
                <div className="fw-medium text-truncate" title={formatWhen(item)}>
                  {formatWhen(item)}
                </div>
                <Badge color={statusColor(item.status)} className="mt-1">
                  {statusLabel(item.status)}
                </Badge>
                {pending ? (
                  <Spinner size="sm" color="warning" className="ms-2" />
                ) : null}
                </div>
              </div>
              {item.status === MediaStatusEnum.FAILED ? (
                <Button
                  color="danger"
                  size="sm"
                  outline
                  disabled={retryingId === item.id}
                  onClick={() => retryConversion(item.id)}
                >
                  {retryingId === item.id ? <Spinner size="sm" /> : t("retry")}
                </Button>
              ) : null}
            </div>
          );
        })}
      </div>

      <Modal
        isOpen={Boolean(playingUrl)}
        toggle={() => setPlaying(null)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setPlaying(null)}>
          {playing
            ? formatWhen(playing)
            : t("liveLectureRecording", { defaultValue: "Recording" })}
        </ModalHeader>
        <ModalBody>
          {playingUrl ? <AppVideoPlayer src={playingUrl} /> : null}
        </ModalBody>
      </Modal>
    </>
  );
};

type LiveLectureRecordingsModalProps = {
  lecture: LiveLectureDto | null;
  show: boolean;
  onClose: () => void;
  langKey: "en" | "ar";
};

export const LiveLectureRecordingsModal: React.FC<
  LiveLectureRecordingsModalProps
> = ({ lecture, show, onClose, langKey }) => {
  const { t } = useTranslation();
  const recordings = lectureRecordings(lecture?.media);

  return (
    <Modal isOpen={show && Boolean(lecture)} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>
        {t("liveLectureRecordings", { defaultValue: "Recordings" })}
        {lecture?.title?.[langKey] ? ` — ${lecture.title[langKey]}` : ""}
      </ModalHeader>
      <ModalBody>
        {show && lecture ? (
          <LiveLectureRecordings key={lecture.id} recordings={recordings} />
        ) : null}
      </ModalBody>
    </Modal>
  );
};
